import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Text, Tooltip } from '@/design-system';
import { Button, type ButtonProps } from '@/design-system/components/Button';
import { useOrganizationId } from '@/hooks/useSwr';
import { useCreateTagsOnResource } from '@/hooks/useTagsOnResource';

import { createDataset } from '../../data/mutations';
import {
  DatasetSchema,
  RepoExampleCreateSchema,
  ResourceType,
  RunSchema,
} from '../../types/schema';
import { Pane } from '../Pane';
import {
  handleAddToDataset,
  handleAddToDatasetManualIO,
} from '../RunsTable/utils/handleAddToDataset';
import useToast from '../Toast/useToast';
import { FileUploadTemplateForm, KVTemplateForm } from './NewDatasetCrudForm';
import { UpdateNonConformingExamplesQueuePane } from './UpdateNonConformingExamplesQueuePane';
import { DatasetTabs } from './components/DatasetTabs';
import {
  DEFAULT_CHAT_INPUT_SCHEMA,
  DEFAULT_CHAT_OUTPUT_SCHEMA,
  DEFAULT_CHAT_TRANSFORMATIONS,
  DEFAULT_SCHEMA,
} from './constants';
import { useDatasetDetails } from './hooks/useDatasetDetails';
import { useDatasetTemplateStore } from './hooks/useDatasetTemplateStore';
import { useFileUploadTemplateStore } from './hooks/useFileUploadTemplateStore';
import { useDatasetSchemaStore } from './hooks/useSchemaStore';
import { useUpdateDataset } from './hooks/useUpdateDataset';
import { useUploadFile } from './hooks/useUploadFile';

export interface BaseProps {
  dataType?: DatasetSchema['data_type'][];
  dataset?: DatasetSchema;
  asLink?: boolean;
  onSuccess?: (id: string, dataset: DatasetSchema) => void;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  disabled?: boolean;
  prefilledData?: Partial<DatasetSchema>;
  baseDatasetOnRunType?: RunSchema['run_type'];
}

type PaneProps = Omit<BaseProps, 'asLink'> & {
  label: string;
  open: boolean;
  onCancel: () => void;
  isKVEnabled?: boolean;
  isChatEnabled?: boolean;
  isLLMEnabled?: boolean;
  preselectedRuns?: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'>[];
  preselectedRunIds?: string[];
  needsConvertPreselectedChatRuns?: boolean;
  isManualIO?: boolean; // Use passed inputs/outputs instead of adding to dataset via run id
} & (
    | {
        isManualIO: true;
        preselectedRuns?: Pick<
          RunSchema,
          'inputs' | 'outputs' | 'extra' | 'id'
        >[];
      }
    | {
        isManualIO?: false;
        preselectedRunIds?: string[];
      }
  );

export function NewDatasetCrudPane({
  dataset,
  onSuccess: propOnSuccess,
  onCancel: _onCancel,
  label,
  open,
  preselectedRuns,
  preselectedRunIds,
  isManualIO = false,
  prefilledData,
  baseDatasetOnRunType,
}: PaneProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const organizationId = useOrganizationId();

  const name = useDatasetDetails((state) => state.name);
  const description = useDatasetDetails((state) => state.description);
  const detailsError = useDatasetDetails((state) => state.error);
  const setDetailsError = useDatasetDetails((state) => state.setError);
  const resetDatasetDetails = useDatasetDetails((state) => state.reset);

  const { createToast } = useToast();

  const inputSchema = useDatasetSchemaStore((state) => state.inputSchema);
  const setInputSchema = useDatasetSchemaStore((state) => state.setInputSchema);
  const outputSchema = useDatasetSchemaStore((state) => state.outputSchema);
  const setOutputSchema = useDatasetSchemaStore(
    (state) => state.setOutputSchema
  );
  const transformations = useDatasetSchemaStore(
    (state) => state.transformations
  );
  const setTransformations = useDatasetSchemaStore(
    (state) => state.setTransformations
  );
  const resetDatasetSchema = useDatasetSchemaStore((state) => state.reset);
  const schemaError = useDatasetSchemaStore((state) => state.error);

  useEffect(() => {
    setInputSchema(
      dataset?.inputs_schema_definition ??
        prefilledData?.inputs_schema_definition ??
        null
    );
    setOutputSchema(
      dataset?.outputs_schema_definition ??
        prefilledData?.outputs_schema_definition ??
        null
    );
    setTransformations(dataset?.transformations);
  }, []);

  const [updatedExamples, setUpdatedExamples] = useState<
    Record<string, RepoExampleCreateSchema>
  >({});
  const [nonConformingExamples, setNonConformingExamples] = useState<
    string[] | null
  >(null);

  const template = useDatasetTemplateStore((state) => state.template);
  const setTemplate = useDatasetTemplateStore((state) => state.setTemplate);

  useEffect(() => {
    if (baseDatasetOnRunType === 'llm') {
      setTemplate('scratch');
      setInputSchema(DEFAULT_CHAT_INPUT_SCHEMA());
      setOutputSchema(DEFAULT_CHAT_OUTPUT_SCHEMA());
      setTransformations(DEFAULT_CHAT_TRANSFORMATIONS);
    } else if (baseDatasetOnRunType) {
      setTemplate('scratch');
      setInputSchema(DEFAULT_SCHEMA('input'));
      setOutputSchema(DEFAULT_SCHEMA('output'));
      setTransformations(undefined);
    } else {
      resetDatasetSchema(dataset);
      resetDatasetDetails(dataset);
    }
  }, [
    baseDatasetOnRunType,
    setTemplate,
    setInputSchema,
    setOutputSchema,
    setTransformations,
    dataset,
    resetDatasetSchema,
    resetDatasetDetails,
  ]);

  const onCSVCancel = useFileUploadTemplateStore((state) => state.onCancel);
  const uploadError = useFileUploadTemplateStore((state) => state.error);
  const uploadFile = useFileUploadTemplateStore((state) => state.file);

  const onCancel = () => {
    onCSVCancel();
    if (dataset) {
      resetDatasetSchema(dataset);
      resetDatasetDetails(dataset);
    } else {
      resetDatasetSchema();
      resetDatasetDetails();
    }
    _onCancel();
  };

  // Clear nonConformingExamples when inputSchema or outputSchema changes (because they are no longer valid)
  useEffect(() => {
    setNonConformingExamples(null);
  }, [inputSchema, outputSchema]);

  const submitDisabledMessage =
    uploadError ||
    detailsError ||
    schemaError ||
    (!dataset &&
      template === 'file' &&
      !uploadFile &&
      'Please upload a file to continue');

  const [nonConformingPaneOpen, setNonConformingPaneOpen] = useState(false);

  const {
    trigger: createTaggings,
    tagsOnResource,
    setTagsOnResource,
  } = useCreateTagsOnResource(ResourceType.Dataset);

  const onSuccess = (id: string, newDataset: DatasetSchema) => {
    propOnSuccess?.(id, newDataset);

    // we're editing
    if (dataset != null) {
      resetDatasetDetails(newDataset);
      resetDatasetSchema(newDataset);
    } else {
      resetDatasetDetails();
      resetDatasetSchema();
    }

    setNonConformingExamples(null);
    setUpdatedExamples({});
    setNonConformingPaneOpen(false);
    onCancel();
  };

  const { upload } = useUploadFile();
  const { update } = useUpdateDataset(dataset);

  const createOrUpdateDataset = (
    updatedExamplesOverride?: Record<string, RepoExampleCreateSchema>
  ) => {
    let promise: Promise<
      DatasetSchema & { headers?: Record<string, string> | undefined }
    >;
    if (!name) {
      createToast({
        title: 'You must provide a dataset name.',
        error: true,
      });
      throw new Error('Name is required.');
    }
    if (!organizationId) {
      createToast({
        title: 'Could not identify your organization.',
        description: 'Please contact us for help.',
        error: true,
      });
      throw new Error(
        'Could not identify your organization. Please contact us for help.'
      );
    }
    if (uploadFile) {
      promise = upload();
    } else {
      promise = dataset
        ? update(updatedExamplesOverride, updatedExamples)
        : createDataset(organizationId, {
            name,
            description,
            data_type: 'kv',
            inputs_schema_definition: inputSchema,
            outputs_schema_definition: outputSchema,
            transformations: transformations,
            extra: {
              source: template === 'file' ? 'upload' : 'manual',
            },
          });
    }
    return promise
      .then(async (data) => {
        // create taggings on dataset creation only.
        if (tagsOnResource && tagsOnResource.length > 0 && !dataset) {
          await createTaggings(data.id);
        }
        if (preselectedRuns || preselectedRunIds) {
          const addToDatasetFunc = isManualIO
            ? () =>
                handleAddToDatasetManualIO(
                  data,
                  preselectedRuns!,
                  organizationId
                )
            : () =>
                handleAddToDataset(data, preselectedRunIds!, organizationId);

          await addToDatasetFunc()
            .then(async (res) => {
              const body = await res.json();
              if (res.ok) {
                onSuccess(data.id, data);
              } else {
                const typedBody: {
                  detail: string | { message: string };
                } = body;

                const msg =
                  typeof typedBody.detail === 'object'
                    ? typedBody.detail.message
                    : typedBody.detail;
                createToast({
                  title:
                    'Successfully created dataset, but failed to add runs.',
                  description: msg,
                  error: true,
                });
              }
            })
            .catch((e) => {
              createToast({
                title: 'Successfully created dataset, but failed to add runs.',
                description: e.message,
                error: true,
              });
            });
        } else {
          onSuccess(data.id, data);
        }
      })
      .catch((e) => {
        let parsedMessage: {
          message: string;
          non_conforming_examples: string[];
          input_errors: string[];
          output_errors: string[];
        } | null = null;
        try {
          parsedMessage = JSON.parse(e.message);
        } catch (e) {
          // do nothing
        }
        if (
          e.message != null &&
          typeof parsedMessage === 'object' &&
          parsedMessage?.['message'] != null
        ) {
          setNonConformingExamples(
            JSON.parse(e.message).non_conforming_examples
          );
        }
        let extra = '';
        if (
          Array.isArray(parsedMessage?.input_errors) &&
          parsedMessage.input_errors.length > 0
        ) {
          extra += `Input errors:\n${parsedMessage.input_errors.join('\n')}`;
        }
        if (
          Array.isArray(parsedMessage?.output_errors) &&
          parsedMessage.output_errors.length > 0
        ) {
          extra += `\nOutput errors:\n${parsedMessage.output_errors.join(
            '\n'
          )}`;
        }
        createToast({
          title: "Couldn't modify dataset.",
          description: parsedMessage?.message ?? e.message,
          children: extra ? (
            <Text variant="xs" className="text-white dark:text-black">
              {extra.slice(0, 1000)}
            </Text>
          ) : null,
          error: true,
        });
      });
  };

  const submitButton = (
    <Button
      variant="normal"
      color={'primary'}
      size="md"
      loading={isSubmitting}
      disabled={!!submitDisabledMessage}
      data-testid="submit-dataset-create-or-edit"
      onClick={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsSubmitting(true);
        try {
          await createOrUpdateDataset();
        } catch (error) {
          setDetailsError((error as Error).message);
        } finally {
          setIsSubmitting(false);
        }
      }}
    >
      {dataset ? 'Submit' : 'Create'}
    </Button>
  );

  const cancelAndSubmitButtons = (
    <div className="mr-4 flex items-center gap-2">
      <div>
        <Button
          onClick={() => {
            // reset all fields on cancel
            onCancel();
          }}
          size="md"
          color="secondary"
          variant="plain"
        >
          Cancel
        </Button>
      </div>
      <div>
        <Tooltip title={submitDisabledMessage}>
          <div className="items-stretch">{submitButton}</div>
        </Tooltip>
      </div>
    </div>
  );

  const openDebounced = useDebounce(open, 600);

  return (
    <Pane
      open={open}
      onClose={onCancel}
      title={label}
      topBarRightElement={cancelAndSubmitButtons}
    >
      {(openDebounced || open) && (
        <div className="flex h-full flex-col">
          {!dataset && <DatasetTabs />}
          {!dataset && template === 'file' ? (
            <FileUploadTemplateForm
              isEditing={false}
              tagsOnResource={tagsOnResource}
              setTagsOnResource={setTagsOnResource}
            />
          ) : (
            <KVTemplateForm
              isEditing={!!dataset}
              nonConformingExamples={nonConformingExamples ?? []}
              existingDataset={dataset}
              setNonConformingPaneOpen={setNonConformingPaneOpen}
              tagsOnResource={tagsOnResource}
              setTagsOnResource={setTagsOnResource}
            />
          )}
        </div>
      )}
      {dataset && nonConformingExamples && (
        <UpdateNonConformingExamplesQueuePane
          isOpen={nonConformingPaneOpen}
          onClose={() => setNonConformingPaneOpen(false)}
          updatedExamples={updatedExamples}
          nonConformingExamples={nonConformingExamples ?? []}
          setUpdatedExamples={setUpdatedExamples}
          inputsSchemaDefinition={inputSchema}
          outputsSchemaDefinition={outputSchema}
          datasetId={dataset.id}
          onSubmit={createOrUpdateDataset}
        />
      )}
    </Pane>
  );
}
